import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import VillaDetailsModal from '../components/VillaDetailsModal';
import VillaBookingModal from '../components/VillaBookingModal';

const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

const Villas = () => {
    const [villas, setVillas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ country: '', propertyType: '', guests: '', minPrice: '', maxPrice: '' });
    const [filterOptions, setFilterOptions] = useState({ countries: [], propertyTypes: [] });
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [selectedVilla, setSelectedVilla] = useState(null);
    const [bookingVilla, setBookingVilla] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [bookingOpen, setBookingOpen] = useState(false);

    const fetchVillas = async (page = 1, newFilters = filters) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({ page, limit: 12 });
            if (newFilters.country) params.append('country', newFilters.country);
            if (newFilters.propertyType) params.append('propertyType', newFilters.propertyType);
            if (newFilters.guests) params.append('guests', newFilters.guests);
            if (newFilters.minPrice) params.append('minPrice', newFilters.minPrice);
            if (newFilters.maxPrice) params.append('maxPrice', newFilters.maxPrice);

            const res = await axios.get(`/api/villas?${params}`);
            if (page === 1) {
                setVillas(res.data.villas || []);
            } else {
                setVillas(prev => [...prev, ...(res.data.villas || [])]);
            }
            setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
        } catch (err) {
            console.error('Error fetching villas:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchFilters = async () => {
        try {
            const res = await axios.get('/api/villas/filters');
            setFilterOptions(res.data);
        } catch (err) {
            console.error('Error fetching filter options:', err);
        }
    };

    useEffect(() => {
        fetchVillas(1, filters);
        fetchFilters();
    }, []);

    const handleFilterChange = (field, value) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
        fetchVillas(1, newFilters);
    };

    const loadMore = () => {
        if (pagination.page < pagination.pages) {
            fetchVillas(pagination.page + 1);
        }
    };

    const clearFilters = () => {
        const empty = { country: '', propertyType: '', guests: '', minPrice: '', maxPrice: '' };
        setFilters(empty);
        fetchVillas(1, empty);
    };

    const getImage = (villa) => {
        if (villa.images?.length) {
            const img = villa.images[0];
            return img.startsWith('/') ? `${API_URL}${img}` : img;
        }
        return 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&h=600&fit=crop';
    };

    const formatPrice = (price) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(price);

    const hasFilters = Object.values(filters).some(v => v);

    return (
        <>
            <div>
                <Navigation />

                {/* Hero Section */}
                <section className="relative mt-20 py-24 overflow-hidden">
                    <div className="absolute inset-0">
                        <img src="https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=1920&h=700&fit=crop" alt="Villas" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/80 to-brand-teal/60"></div>
                    </div>
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
                        <h1 className="text-5xl sm:text-6xl font-bold mb-4 leading-tight">Villas & Bungalows</h1>
                        <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-8">Discover luxury properties for your perfect getaway — private villas and charming bungalows across India and beyond</p>
                        <div className="flex justify-center gap-8 text-center">
                            <div><div className="text-4xl font-bold">{pagination.total}+</div><div className="text-gray-200">Properties</div></div>
                            <div><div className="text-4xl font-bold">2</div><div className="text-gray-200">Types</div></div>
                        </div>
                    </div>
                </section>

                {/* Filters */}
                <section className="py-8 bg-gray-50 border-b sticky top-20 z-10 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-wrap gap-3 items-end">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Country</label>
                                <select value={filters.country} onChange={e => handleFilterChange('country', e.target.value)} className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none bg-white text-sm">
                                    <option value="">All Countries</option>
                                    {filterOptions.countries.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Type</label>
                                <select value={filters.propertyType} onChange={e => handleFilterChange('propertyType', e.target.value)} className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none bg-white text-sm">
                                    <option value="">All Types</option>
                                    <option value="villa">Villa</option>
                                    <option value="bungalow">Bungalow</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Guests</label>
                                <input type="number" min="1" placeholder="Min guests" value={filters.guests} onChange={e => handleFilterChange('guests', e.target.value)} className="w-28 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none bg-white text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Min Price/Night (₹)</label>
                                <input type="number" min="0" placeholder="₹ Min" value={filters.minPrice} onChange={e => handleFilterChange('minPrice', e.target.value)} className="w-28 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none bg-white text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Max Price/Night (₹)</label>
                                <input type="number" min="0" placeholder="₹ Max" value={filters.maxPrice} onChange={e => handleFilterChange('maxPrice', e.target.value)} className="w-28 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none bg-white text-sm" />
                            </div>
                            {hasFilters && (
                                <button onClick={clearFilters} className="px-4 py-2 text-sm text-red-600 hover:text-red-800 font-medium border border-red-200 rounded-lg hover:bg-red-50 transition">
                                    <i className="fas fa-times mr-1"></i>Clear Filters
                                </button>
                            )}
                        </div>
                    </div>
                </section>

                {/* Grid */}
                <section className="py-16 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-2xl font-bold text-gray-800">{pagination.total} {hasFilters ? 'Matching' : ''} Properties</h2>
                        </div>

                        {loading && villas.length === 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="rounded-2xl overflow-hidden animate-pulse bg-gray-200 h-80"></div>
                                ))}
                            </div>
                        ) : villas.length === 0 ? (
                            <div className="text-center py-20">
                                <i className="fas fa-home text-5xl text-gray-300 mb-4"></i>
                                <p className="text-xl text-gray-500">No properties found matching your filters.</p>
                                {hasFilters && <button onClick={clearFilters} className="mt-4 text-brand-teal hover:underline">Clear all filters</button>}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {villas.map(villa => (
                                    <div key={villa.id || villa._id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover-lift card-shine border border-gray-100">
                                        <div className="relative h-60 overflow-hidden hover-zoom">
                                            <img src={getImage(villa)} alt={villa.title} className="w-full h-full object-cover transition duration-700" />
                                            <div className="absolute top-4 left-4 flex gap-2">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize text-white ${villa.propertyType === 'villa' ? 'bg-purple-600' : 'bg-orange-500'}`}>
                                                    {villa.propertyType}
                                                </span>
                                                {villa.isFeatured && <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-semibold">Featured</span>}
                                            </div>
                                            {villa.rating > 0 && (
                                                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-sm font-semibold text-gray-800">
                                                    <i className="fas fa-star text-yellow-400 mr-1"></i>{villa.rating}
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-5">
                                            <h3 className="text-xl font-bold text-gray-800 mb-1">{villa.title}</h3>
                                            <p className="text-gray-500 text-sm mb-3"><i className="fas fa-map-marker-alt text-brand-teal mr-1"></i>{villa.location}, {villa.country}</p>
                                            <div className="flex gap-4 text-sm text-gray-500 mb-4">
                                                <span><i className="fas fa-users text-brand-teal mr-1"></i>{villa.maxGuests} Guests</span>
                                                <span><i className="fas fa-bed text-brand-teal mr-1"></i>{villa.bedrooms} Beds</span>
                                                <span><i className="fas fa-bath text-brand-teal mr-1"></i>{villa.bathrooms} Baths</span>
                                            </div>
                                            {villa.amenities?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mb-4">
                                                    {villa.amenities.slice(0, 3).map((a, i) => (
                                                        <span key={i} className="bg-teal-50 text-brand-teal px-2 py-1 rounded text-xs">{a}</span>
                                                    ))}
                                                    {villa.amenities.length > 3 && <span className="bg-gray-100 text-gray-500 px-2 py-1 rounded text-xs">+{villa.amenities.length - 3} more</span>}
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                                <div>
                                                    <span className="text-xs text-gray-400">From</span>
                                                    <div className="text-2xl font-bold bg-gradient-to-r from-brand-teal to-brand-cyan bg-clip-text text-transparent">{formatPrice(villa.pricePerNight)}</div>
                                                    <span className="text-xs text-gray-400">per night</span>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setSelectedVilla(villa); setDetailsOpen(true); }}
                                                        className="relative z-10 border-2 border-brand-teal text-brand-teal px-3 py-2 rounded-full text-sm font-semibold hover:bg-brand-teal hover:text-white transition"
                                                    >
                                                        <i className="fas fa-eye mr-1"></i>Details
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setBookingVilla(villa); setBookingOpen(true); }}
                                                        className="relative z-10 bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-4 py-2 rounded-full text-sm font-semibold hover:shadow-lg transition"
                                                    >
                                                        Book Now
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Load More */}
                        {pagination.page < pagination.pages && (
                            <div className="text-center mt-10">
                                <button onClick={loadMore} disabled={loading} className="bg-white border-2 border-brand-teal text-brand-teal px-8 py-4 rounded-full font-semibold hover:bg-brand-teal hover:text-white transition transform hover:scale-105 disabled:opacity-60">
                                    {loading ? <><i className="fas fa-spinner fa-spin mr-2"></i>Loading...</> : 'Load More Properties'}
                                </button>
                            </div>
                        )}
                    </div>
                </section>

                <Footer />
            </div>

            <VillaDetailsModal
                isOpen={detailsOpen}
                onClose={() => { setDetailsOpen(false); setSelectedVilla(null); }}
                villa={selectedVilla}
                villaId={selectedVilla?.id || selectedVilla?._id}
                onBook={(v) => { setDetailsOpen(false); setBookingVilla(v); setBookingOpen(true); }}
            />
            <VillaBookingModal
                isOpen={bookingOpen}
                onClose={() => setBookingOpen(false)}
                villa={bookingVilla}
            />
        </>
    );
};

export default Villas;
